import { HOLIDAY_DAY_PARTS, type HolidayPerson } from "./Holidays/holidayApi";
import { HOLIDAY_NONE } from "./Holidays/holidayFormat";

/**
 * The Team page's two holiday facets: Holiday group (each group + Not assigned) and Holiday day part (each
 * day part + No day part). People come from the holiday people list, keyed by nurse id; until it loads
 * (`null`) a narrowed facet matches nobody, and the page shows skeleton rows instead of an empty roster.
 */

/** Every value the day part facet can hold — anything else in the URL is stale and dropped. */
export const HOLIDAY_DAY_PART_FILTER_IDS: readonly string[] = [...HOLIDAY_DAY_PARTS, HOLIDAY_NONE];

/** A person's group facet value: their group, or Not assigned. */
export const holidayGroupFacetKey = (person: HolidayPerson): string => person.groupId ?? HOLIDAY_NONE;

/**
 * A person's day part facet value. Only grouped people have one: No day part means grouped with no shifts in
 * the last 6 weeks, so someone with no group is in none of these options.
 */
export const holidayDayPartFacetKey = (person: HolidayPerson): string | null => (person.groupId === null ? null : (person.dayPart ?? HOLIDAY_NONE));

/** OR'ed group selection: empty = unrestricted. */
export function matchesHolidayGroupSelection(nurseId: number, selected: ReadonlySet<string>, byNurseId: ReadonlyMap<number, HolidayPerson> | null): boolean {
	if (selected.size === 0) return true;
	const person = byNurseId?.get(nurseId);
	return person !== undefined && selected.has(holidayGroupFacetKey(person));
}

/** OR'ed day part selection: empty = unrestricted. */
export function matchesHolidayDayPartSelection(nurseId: number, selected: ReadonlySet<string>, byNurseId: ReadonlyMap<number, HolidayPerson> | null): boolean {
	if (selected.size === 0) return true;
	const person = byNurseId?.get(nurseId);
	if (person === undefined) return false;
	const key = holidayDayPartFacetKey(person);
	return key !== null && selected.has(key);
}
